/**
 * AboutDialog — modal showing app name, version, description, and support contact.
 *
 * Version is a static string (0.1.0) per the slice brief.
 * Adding a Vite define for __APP_VERSION__ is deferred to a future polish slice.
 */
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Static version — replace with import.meta.env.PACKAGE_VERSION once
// a Vite define is wired in a future polish slice.
const APP_VERSION = '0.1.0';

// TODO(ui-generator): replace hardcoded support email with a config constant
// once the backend exposes a support contact endpoint or env var.
const SUPPORT_EMAIL = 'support@southernstarmga.com';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function AboutDialog({ open, onClose }: Props) {
  const { t, i18n } = useTranslation();

  const buildDate = new Date().toLocaleDateString(i18n.language);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="about-dialog-title"
    >
      <DialogTitle id="about-dialog-title">
        <Stack direction="row" spacing={1} alignItems="center">
          <InfoOutlinedIcon color="primary" />
          <span>{t('about.title')}</span>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="h6" color="primary">
            {t('app.title')}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t('about.description')}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t('about.phase')}
          </Typography>

          <Stack direction="row" spacing={1}>
            <Typography variant="body2" fontWeight="medium">
              {t('about.version')}:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {APP_VERSION}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Typography variant="body2" fontWeight="medium">
              Build:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {buildDate}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight="medium">
              {t('about.support')}:
            </Typography>
            <Link
              href={`mailto:${SUPPORT_EMAIL}`}
              variant="body2"
              underline="hover"
            >
              {SUPPORT_EMAIL}
            </Link>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" disableElevation>
          {t('about.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
